<template>
    <article>
        <SectionLabel :label="trads.label"></SectionLabel>
        <Loader v-if="loading" :loading="true"></Loader>

        <section id="default-campaign">
            <label required>{{ trads.defaultLaunchAndDiscontinuation }}</label>
            <select v-model="value.startCampaignId">
                <option :value="undefined">–––</option>
                <option
                    v-for="campaign in campaigns"
                    :key="campaign.id"
                    :value="campaign.id"
                    :disabled="!isValidCampaignRange({ startId: campaign.id, endId: value.endCampaignId })"
                    >{{ campaign.name }}</option
                >
            </select>
            <select v-model="value.endCampaignId">
                <option :value="undefined">{{ trads.onGoing }}</option>
                <option
                    v-for="campaign in campaigns"
                    :key="campaign.id"
                    :value="campaign.id"
                    :disabled="!isValidCampaignRange({ startId: value.startCampaignId, endId: campaign.id, allowEquals: false })"
                    >{{ campaign.name }}</option
                >
            </select>
        </section>

        <section>
            <label required>{{ trads.zonesAndMarkets }}</label>
            <ul id="zones">
                <!-- .checked class used to hide the selects on mobile format -->
                <li
                    v-if="!isDisabledZone(zone.id)"
                    v-for="zone in zones"
                    :key="zone.key"
                    :class="{ checked: value.zoneDetailsPerId[zone.id] && value.zoneDetailsPerId[zone.id].checked }"
                >
                    <span>
                        <input
                            type="checkbox"
                            :id="`zone-${zone.id}`"
                            :checked="value.zoneDetailsPerId[zone.id] && value.zoneDetailsPerId[zone.id].checked"
                            @input="e => setZone(zone.id, { checked: e.target.checked })"
                        />
                        <label class="d-inline" :for="`zone-${zone.id}`">{{ zone.name }}</label>
                    </span>

                    <select
                        :disabled="!value.zoneDetailsPerId[zone.id] || !value.zoneDetailsPerId[zone.id].checked"
                        :value="zoneStartId(zone.id)"
                        @input="e => setZone(zone.id, { startCampaignId: Number.parseInt(e.target.value) })"
                    >
                        <option :value="undefined">–––</option>
                        <option
                            v-for="campaign in campaigns"
                            :key="campaign.id"
                            :disabled="!isValidCampaignRange({ startId: campaign.id, endId: zoneEndId(zone.id) })"
                            :value="campaign.id"
                        >
                            {{ campaign.name }}
                        </option>
                    </select>
                    <select
                        :disabled="!value.zoneDetailsPerId[zone.id] || !value.zoneDetailsPerId[zone.id].checked"
                        :value="zoneEndId(zone.id)"
                        @input="e => setZone(zone.id, { endCampaignId: (e.target.value && Number.parseInt(e.target.value, 10)) || undefined })"
                    >
                        <option :value="undefined">{{ trads.onGoing }}</option>
                        <option
                            v-for="campaign in campaigns"
                            :key="campaign.id"
                            :disabled="!isValidCampaignRange({ startId: zoneStartId(zone.id), endId: campaign.id, allowEquals: false })"
                            :value="campaign.id"
                            >{{ campaign.name }}</option
                        >
                    </select>
                </li>
                <li v-if="isDisabledZone(zone.id)" v-for="zone in zones" :key="zone.key">
                    <span class="font-weight-bold">{{ zone.name }} <i>(DELETED)</i></span>
                </li>
            </ul>
            <label>{{ trads.description }}</label>
            <TranslationInput id="description" :area="true" v-model="value.description"></TranslationInput>
            <label>{{ trads.status }}</label>
            <RadioGroup id="status" v-model="value.statusId" name="status" :options="statusOptions"></RadioGroup>
            <label required>{{ trads.categories }}</label>
            <span id="categories">
                <ul>
                    <li v-for="(pair, i) in selectedCategories" :key="i">
                        <select
                            v-for="(item, index) in pair"
                            :key="forceRefreshCategoriesPicker"
                            :value="pair[index]"
                            @input="updateCategory(i, index, $event.target.value);"
                        >
                            <option :value="undefined">–––</option>
                            <option v-for="category in findListCategories(item, pair)" :key="category.id" :value="category.id">{{
                                category.terms.name[$locale]
                            }}</option>
                        </select>
                        <select
                            v-if="findListCategories(null, pair).length"
                            :key="forceRefreshCategoriesPicker"
                            :value="undefined"
                            @input="updateCategory(i, pair.length, $event.target.value);"
                        >
                            <option :value="undefined">–––</option>
                            <option v-for="category in findListCategories(null, pair)" :key="category.id" :value="category.id">{{
                                category.terms.name[$locale]
                            }}</option>
                        </select>

                        <button @click="removeCategory(pair);">x</button>
                    </li>
                </ul>
                <button id="add-category" @click="addCategory">{{ trads.addCategory }}</button>
            </span>
        </section>
        <FormEstore
            v-if="zones.length > 0"
            v-model="value.eStoreData"
            :zones="zones"
            :brandZones="brandZones"
            :zonesChecked="value.zoneDetailsPerId"
        ></FormEstore>
    </article>
</template>

<script>
import TranslationInput from "./components/translation-input";
import RadioGroup from "./components/radio-group";
import SectionLabel from "./components/section-label";
import zoneService from "@/services/zone";
import campaignService from "@/services/campaign";
import statusService from "@/services/status";
import categoriesService from "@/services/categories";
import TryLoadMixin from "@/mixins/TryLoadMixin";
import Loader from "@/components/loader/loader";
import FormEstore from "./form-estore";
import { mapActions, mapGetters } from "vuex";

export default {
    mixins: [TryLoadMixin],
    components: { TranslationInput, RadioGroup, SectionLabel, Loader, FormEstore },
    props: {
        value: { type: Object, required: true }
    },
    data: () => ({
        zones: [],
        campaigns: [],
        allStatus: [],
        categories: [],
        selectedCategories: [],
        // for some reasons, <select> do not update to properly display the selected value when adding options without changing :value
        // so we are forcing the refresh using keys
        // This is likely because vue does not properly detect a change on "data.categories" but I did not have the time to investigate
        forceRefreshCategoriesPicker: 0
    }),
    async created() {
        this.zones = await this.tryLoad(() => zoneService.getAll());
        this.campaigns = await this.tryLoad(() => campaignService.getAll());
        this.allStatus = await this.tryLoad(() => statusService.getAll());
        this.categories = await this.tryLoad(() => categoriesService.getAll());
        await this.tryLoad(() => this.populateAdminBrandZones({ reload: true }));

        this.selectedCategories = this.value.categories;
        let myCats = this.selectedCategories.map(pair => {
            return {
                category: { id: (pair.length && pair[pair.length - 1]) || null }
            };
        });
        this.$emit("changeCategories", myCats);
    },
    watch: {
        categories() {
            this.forceRefreshCategoriesPicker++;
        },
        "value.startCampaignId"() {
            const newValue = {};
            this.value.endCampaignId = undefined;
            for (const zoneId of Object.keys(this.value.zoneDetailsPerId)) {
                newValue[zoneId] = {
                    ...this.value.zoneDetailsPerId[zoneId],
                    startCampaignId: undefined,
                    endCampaignId: undefined
                };
            }
            this.value.zoneDetailsPerId = newValue;
        },
        "value.endCampaignId"(value) {
            const newValue = {};
            for (const zone of this.zones) {
                newValue[zone.id] = {
                    ...this.value.zoneDetailsPerId[zone.id],
                    endCampaignId: value
                };
            }
            this.value.zoneDetailsPerId = newValue;
        }
    },
    computed: {
        ...mapGetters("forecast", { brandZones: "getAdminBrandZones" }),
        trads() {
            return this.$trad.admin.componentForm.defaultCampaign;
        },
        statusOptions() {
            return [
                { value: undefined, label: this.trads.noStatus },
                ...this.allStatus.map(status => {
                    return {
                        value: status.id,
                        label: this.$tradFor(status.terms.name)
                    };
                })
            ];
        }
    },
    methods: {
        ...mapActions("forecast", { populateAdminBrandZones: "populateAdminBrandZones" }),
        zoneStartId(zoneId) {
            return (this.value.zoneDetailsPerId[zoneId] && this.value.zoneDetailsPerId[zoneId].startCampaignId) || this.value.startCampaignId;
        },
        zoneEndId(zoneId) {
            return this.value.zoneDetailsPerId[zoneId] && this.value.zoneDetailsPerId[zoneId].endCampaignId;
        },
        findCampaign(id) {
            return this.campaigns.find(c => c.id === id);
        },
        isValidCampaignRange({ startId, endId, allowEquals }) {
            if (!startId) return false;
            if (startId === endId) {
                return allowEquals || allowEquals === undefined;
            }
            let campaign = this.findCampaign(startId);
            campaign = campaign && campaign.lastCampaign && this.findCampaign(campaign.lastCampaign.id);
            while (campaign) {
                if (campaign.id === endId) {
                    return false;
                }
                campaign = campaign && campaign.lastCampaign && this.findCampaign(campaign.lastCampaign.id);
            }
            return true;
        },
        setZone(zoneId, value) {
            this.value.zoneDetailsPerId = {
                ...this.value.zoneDetailsPerId,
                [zoneId]: { ...this.value.zoneDetailsPerId[zoneId], ...value }
            };
        },
        addCategory() {
            this.selectedCategories = [...this.selectedCategories, []];
        },
        categoryOf(id, categories) {
            console.assert(typeof id === "number" || !id);
            categories = categories || this.categories;
            return categories.find(category => category.id === id);
        },
        findListCategories(id, catIds) {
            console.assert(typeof id === "number" || !id);
            let categories = this.categories;
            let index = 0;

            while (categories.find(category => category.id === catIds[index]) && catIds[index] !== id) {
                categories = categories.find(category => category.id === catIds[index]).subCategories;
                index = index + 1;
            }
            return categories;
        },
        updateCategory(index, indexPair, newValue) {
            let res = [...this.selectedCategories];
            let pair = this.selectedCategories[index];
            pair = (pair instanceof Array && pair.slice(0, indexPair)) || [];
            let categories = this.categories;
            let i = 0;
            while (categories.find(category => category.id === pair[i]) && i <= indexPair) {
                categories = categories.find(category => category.id === pair[i]).subCategories;
                i++;
            }
            if (categories.find(category => category.id === parseInt(newValue))) {
                pair.push(parseInt(newValue));
            }
            res[index] = pair;
            this.selectedCategories = res;
            let myCats = this.selectedCategories.map(pair => {
                return {
                    category: { id: (pair.length && pair[pair.length - 1]) || null }
                };
            });
            this.$emit("changeCategories", myCats);
        },
        removeCategory(pair) {
            const res = [...this.selectedCategories];
            const index = this.selectedCategories.findIndex(c => c === pair);
            res.splice(index, 1);

            this.selectedCategories = res;
            let myCats = this.selectedCategories.map(pair => {
                return {
                    category: { id: (pair.length && pair[pair.length - 1]) || null }
                };
            });
            this.$emit("changeCategories", myCats);
        },
        isDisabledZone(zoneId) {
            const brandZone = this.brandZones.find(bz => bz.zone.id === zoneId);
            if (!brandZone) {
                return true;
            }
            return !brandZone.active;
        }
    }
};
</script>

<style lang="scss">
#main #component-form {
    article {
        position: relative;
    }

    @media (min-width: 767px) {
        #default-campaign {
            grid-template-columns: 1fr auto auto;
        }

        #estore,
        #status {
            width: 100%;
        }
    }

    #zones {
        justify-self: stretch;

        li {
            display: grid;
            align-items: center;
            grid-template-columns: 1fr auto auto;
            grid-column-gap: 20px;
            min-height: 25px;

            @media (max-width: 767px) {
                grid-template-columns: 1fr;
                height: unset;
                grid-row-gap: 5px;
                padding-bottom: 15px;

                &:not(.checked) {
                    padding-bottom: 0;
                    > select {
                        display: none;
                    }
                }
            }
        }
    }

    #description {
        width: 100%;
    }

    #categories {
        #add-category {
            background-color: #777777;
            padding: 2px 6px;
            color: white;
            border: none;
            border-radius: 4px;
            text-transform: uppercase;
            font-size: 12px;
        }

        ul {
            margin-block-end: 8px;

            li {
                align-items: center;
                font-weight: bold;
                & + li {
                    margin-top: 4px;
                }

                > button {
                    margin-left: 8px;
                    border-radius: 8px;
                }
            }
        }
    }
}
</style>



// WEBPACK FOOTER //
// form-default-campaign.vue?412bbd7c